// Knowledge Management Types
import { nanoid } from 'nanoid';

export type ProductImageCategory = 'brand_logo' | 'product_image' | 'material';

/** LP 自動登録で画像を見て付く削除候補の理由（アイコン・他社のロゴ・商品と関係のない画像・同じページの別の商品） */
export type ProductImageRemovalReason = 'icon' | 'other_brand' | 'unrelated' | 'other_product';

export interface ProductImage {
  id: number;
  product_id: number;
  category: ProductImageCategory;
  title?: string;
  description?: string;
  file_path: string;
  file_name: string;
  mime_type?: string;
  file_size?: number;
  sort_order: number;
  is_primary: boolean;
  url: string;
  created_at: string;
  updated_at: string;
  // Only populated for category === 'brand_logo'. null/undefined = tone not yet classified.
  logo_tone?: 'light' | 'dark' | 'color' | null;
  // 削除候補。バナーでは後ろに回り、ロゴ・主役には選ばれない。null で候補から外れる
  removal_suggestion?: ProductImageRemovalReason | null;
}

/**
 * バナー作成の商品ピッカーが最初に選んでおく画像（MCP と同じ素材の役割判定）。
 * category は参照画像として渡すときの値（主役は product_image、自社ロゴは brand_logo）。
 */
export interface BannerAssetDefaults {
  product_id: number;
  primary_image_id: number | null;
  logo_image_id: number | null;
  default_images: Array<{ image_id: number; role: string; category: ProductImageCategory }>;
  images: Array<{ image_id: number; role: string | null; basis: string | null }>;
}

// Extended metadata types for product marketing information (from product.ts)
export interface ProductInfo {
  name?: string;
  brand?: string;
  category?: string;
  market_type?: string;
  sales_model?: string;
  tagline?: string;
  description?: string;
  usp?: string;
  development_structure?: string;
  use_scenarios?: string;
  variations?: string;
}

export interface PricingOffer {
  main_cta?: string;
  price_visibility?: string;
  price_details?: string;
  offers_campaigns?: string;
}

export interface TargetInsights {
  pain_emotion?: string;
  ideal_state?: string;
  barriers?: string;
  purchase_factor?: string;
}

export interface Target {
  segment?: string;
  insights?: TargetInsights;
}

export interface Benefits {
  functional?: string;
  emotional?: string;
  self_realization?: string;
}

export interface TrustProof {
  statement?: string;
  source?: string;
  url?: string;
  title?: string;
}

export interface Positioning {
  /** ②: 「◯◯なら、{商品名}」の「◯◯」に入るポジショニング文（USPと商品内容を統合した専用フィールド） */
  statement_lead?: string;
  pop?: string[];
  pod?: string[];
  trust_proof?: (TrustProof | string)[];
}

export interface Appeal {
  recommended_types?: string[];
  direction?: string;
  winning_copy?: string;
  structure_pattern?: string;
}

// ========================================
// Segment Types (Multiple Segments Support)
// ========================================

/** DCMファネル層 */
export type DcmFunnelStageId = 'latent' | 'semi_latent' | 'semi_manifest' | 'manifest';

/** 訴求タイプ */
export type AppealType =
  | 'functional'
  | 'value'
  | 'trust'
  | 'feature'
  | 'merit'
  | 'benefit'
  | 'emotion'
  | 'negative'
  | 'offer'
  | 'comparison'
  | 'social';

/** 訴求タイプのラベル */
export const APPEAL_TYPE_LABELS: Record<AppealType, string> = {
  functional: '機能・特長訴求',
  value: '価値訴求',
  trust: '信頼性訴求',
  feature: '機能・特長訴求',
  merit: 'メリット訴求',
  benefit: 'ベネフィット訴求',
  emotion: '感情・心理訴求',
  negative: 'ネガティブ訴求',
  offer: 'オファー訴求',
  comparison: '比較訴求',
  social: '社会貢献訴求',
};

/** DCM訴求軸 */
export const DCM_APPEAL_AXIS_OPTIONS = {
  feature: ['基本性能', '操作性', '追加機能', '品質強調', '革新性'],
  merit: ['時間的メリット', '経済的メリット', '性能的メリット', '利便性'],
  benefit: ['機能的ベネフィット', '情緒的ベネフィット', '自己実現ベネフィット', '未来志向のベネフィット'],
  trust: ['利用実績', '第三者評価', '保証・サポート'],
  emotion: ['悩み共感', '緊急性', '限定感', '損失・リスク'],
  negative: ['経済的リスク', '健康リスク', '時間の無駄'],
  offer: ['割引', '送料無料', '特典', '無料トライアル'],
  comparison: ['直接競合比較', '間接競合比較'],
  social: ['環境配慮', '社会貢献', '持続可能性'],
} as const;

/**
 * ファネル層の選択肢。
 * ⑤: ラベルを「潜在層 / 準顕在層 / 顕在層 / 明確層」に改名（IDは維持・既存データ互換のためラベルのみシフト）。
 *   旧ラベル → 新ラベル: 潜在層→潜在層（変更なし） / 準潜在層→準顕在層 / 準顕在層→顕在層 / 顕在層→明確層
 * value（funnel_stage_id）はDBに保存されるため変更しない。表示のみ新ラベルに揃える。
 */
export const FUNNEL_STAGE_OPTIONS = [
  { value: 'latent', label: '潜在層', sub: 'まだ気づいていない' },
  { value: 'semi_latent', label: '準顕在層', sub: '課題に気づいた' },
  { value: 'semi_manifest', label: '顕在層', sub: '比べている' },
  { value: 'manifest', label: '明確層', sub: '指名で探している' },
] as const;

/**
 * ファネル層の見た目マスタ（戦略マップの固定列＝ファネルラベルの色/アイコン）。
 * 潜在（グレー）→顕在（primary濃）へ段階的。値は DCM モック knowledge-dcm-scoped.html の FUNNELS 準拠。
 */
export const DCM_FUNNEL_VISUAL: Record<
  DcmFunnelStageId,
  { icon: string; labelBg: string; labelColor: string; subColor: string; iconColor: string }
> = {
  latent: { icon: 'visibility_off', labelBg: '#E7E9ED', labelColor: '#5C6572', subColor: '#959CA8', iconColor: '#9AA1AD' },
  semi_latent: { icon: 'lightbulb', labelBg: '#D6DBF1', labelColor: '#4A55A6', subColor: '#7C86C8', iconColor: '#6B75C5' },
  semi_manifest: { icon: 'compare_arrows', labelBg: '#A7B1E4', labelColor: '#2A3170', subColor: '#414A92', iconColor: '#39418A' },
  manifest: { icon: 'local_fire_department', labelBg: '#616FC9', labelColor: '#FFFFFF', subColor: '#DEE2F6', iconColor: '#FFFFFF' },
};

/** 性別の選択肢 */
export const GENDER_OPTIONS = [
  { value: 'male', label: '男性' },
  { value: 'female', label: '女性' },
  { value: 'any', label: '問わず' },
] as const;

/** テクノロジー対応度の選択肢 */
export const TECH_LITERACY_OPTIONS = [
  { value: 'high', label: '高い' },
  { value: 'medium', label: '中程度' },
  { value: 'low', label: '低い' },
] as const;

/** セグメントインサイト */
export interface SegmentInsights {
  pain_emotion?: string;              // 課題への感情
  ideal_state?: string;               // 理想の状態
  barriers?: string;                  // 障害となる感情
  purchase_factor?: string;           // 購買決定要因
}

/** セグメントベネフィット */
export interface SegmentBenefits {
  functional?: string;                // 機能的ベネフィット
  economic?: string;                  // 経済的ベネフィット
  emotional?: string;                 // 感情的ベネフィット
  social?: string;                    // 社会的ベネフィット
  self_realization?: string;          // 自己実現ベネフィット
  summary?: string;                   // 価値要約
}

/** 訴求軸 */
export interface SegmentAppeal {
  id: string;                         // UUID
  name: string;                       // 訴求名
  type: AppealType;                   // 訴求タイプ
  appeal_axis?: string;               // DCM訴求軸
  direction?: string;                 // 方向性
  copy_example?: string;              // 訴求例文
  copies?: DcmCopy[];                 // コピー案
  order: number;                      // 並び順
}

export interface DcmSegmentMaster {
  segment_id: string;
  segment_name: string;
  definition?: string;
  industry_context?: string;
  usage_context?: string;
  motivation?: string;
  common_situation?: string;
  common_desire?: string;
  common_anxiety?: string;
  common_decision_context?: string;
  notes?: string;
  order: number;
}

export interface DcmFunnelStageMaster {
  funnel_stage_id: DcmFunnelStageId;
  label: string;
  sub?: string;
  order: number;
}

export interface DcmDemo {
  gender?: string;
  age_range?: string;
  region?: string;
  occupation?: string;
  purchasing_power?: string;
  tech_literacy?: string;
  life_stage?: string;
}

export interface DcmPersona {
  name?: string;     // 代表人物名（例: 田村 さやか）
  age?: string;      // 例: 29歳
  tagline?: string;  // 例: 東京在住・メーカー勤務
  story?: string;
  situation?: string;
  desire?: string;
  anxiety?: string;
  decision_context?: string;
}

export interface DcmEmotionMap {
  pain_emotion?: string;
  ideal_state?: string;
  blocking_emotion?: string;
  decision_factor?: string;
}

export interface DcmValue {
  functional_value?: string;
  economic_value?: string;
  emotional_value?: string;
  social_value?: string;
  summary?: string;
}

export interface DcmCopy {
  id: string;
  text: string;
  use: boolean;
}

export interface DcmAppeal {
  appeal_id: string;
  name: string;
  type: AppealType;
  appeal_axis?: string;
  hook?: string;        // この訴求が突く感情（狙い文の「」赤強調部分）
  direction?: string;   // 狙い（一文・DCMモックの reason に相当）
  copies: DcmCopy[];
  order: number;
}

export interface DcmWho {
  who_id: string;
  segment_id: string;
  funnel_stage_id: DcmFunnelStageId;
  who_name: string;
  demo?: DcmDemo;
  persona?: DcmPersona;
  emotion_map?: DcmEmotionMap;
  value?: DcmValue;
  appeals: DcmAppeal[];
  order?: number;
}

export interface DcmSource {
  url?: string;
  title?: string;
  source?: string;
  statement?: string;
}

export interface DcmViewModel {
  segmentMaster: DcmSegmentMaster[];
  funnelStageMaster: DcmFunnelStageMaster[];
  who: DcmWho[];
  source: DcmSource[];
}

/** セグメント情報 */
export interface Segment {
  id: string;                         // UUID (nanoid使用)
  name: string;                       // セグメント名
  funnel_stage?: string;              // ファネル層
  gender?: string;                    // 性別
  age_range?: string;                 // 年代
  region?: string;                    // 地域
  occupation?: string;                // 職業・業界
  purchasing_power?: string;          // 購買力
  tech_literacy?: string;             // テクノロジー対応度
  life_stage?: string;                // ライフステージ
  insights: SegmentInsights;          // インサイト
  benefits: SegmentBenefits;          // ベネフィット
  appeals: SegmentAppeal[];           // 訴求軸（複数）
  order: number;                      // 並び順
  created_at?: string;
  updated_at?: string;
}

/** デフォルト訴求生成 */
export const createDefaultAppeal = (order: number): SegmentAppeal => ({
  id: nanoid(),
  name: `訴求${order + 1}`,
  type: 'functional',
  direction: '',
  copy_example: '',
  order,
});

/** デフォルトセグメント生成 */
export const createDefaultSegment = (order: number): Segment => ({
  id: nanoid(),
  name: `セグメント${order + 1}`,
  funnel_stage: '',
  gender: '',
  age_range: '',
  region: '',
  occupation: '',
  purchasing_power: '',
  tech_literacy: '',
  life_stage: '',
  insights: {
    pain_emotion: '',
    ideal_state: '',
    barriers: '',
    purchase_factor: '',
  },
  benefits: {
    functional: '',
    emotional: '',
    self_realization: '',
  },
  appeals: [
    createDefaultAppeal(0),
    createDefaultAppeal(1),
    createDefaultAppeal(2),
  ],
  order,
});

/** デフォルトセグメント配列生成（初期3つ） */
export const createDefaultSegments = (): Segment[] => [
  createDefaultSegment(0),
  createDefaultSegment(1),
  createDefaultSegment(2),
];

/** API訴求タイプからフロントエンド訴求タイプへの変換 */
const mapAppealType = (apiType?: string): AppealType => {
  if (!apiType) return 'functional';
  const lower = apiType.toLowerCase();
  if (lower.includes('信頼') || lower.includes('実績') || lower.includes('trust')) {
    return 'trust';
  }
  if (lower.includes('価値') || lower.includes('ベネフィット') || lower.includes('value')) {
    return 'value';
  }
  return 'functional';
};

/** API形式の訴求データからSegmentAppeal[]への変換 */
const migrateAppeals = (apiAppeals?: any[]): SegmentAppeal[] => {
  if (!apiAppeals || !Array.isArray(apiAppeals) || apiAppeals.length === 0) {
    return [createDefaultAppeal(0)];
  }

  return apiAppeals.map((appeal, index) => ({
    id: nanoid(),
    name: appeal.appeal_name || `訴求${index + 1}`,
    type: mapAppealType(appeal.type),
    direction: appeal.direction || '',
    // copy_example_1, copy_example_2, copy_example_3 を結合
    copy_example: [
      appeal.copy_example_1,
      appeal.copy_example_2,
      appeal.copy_example_3,
    ].filter(Boolean).join('\n') || appeal.copy_example || '',
    order: index,
  }));
};

/** API形式のターゲットデータからSegmentへの変換 */
const migrateTargetToSegment = (apiTarget: any, index: number): Segment => {
  return {
    id: nanoid(),
    name: apiTarget.segment || `セグメント${index + 1}`,
    funnel_stage: apiTarget.funnel_stage || '',
    gender: apiTarget.gender || '',
    age_range: apiTarget.age_range || '',
    region: apiTarget.region || '',
    occupation: apiTarget.occupation_industry || apiTarget.occupation || '',
    purchasing_power: apiTarget.purchasing_power || '',
    tech_literacy: apiTarget.tech_adoption || apiTarget.tech_literacy || '',
    life_stage: apiTarget.life_stage || '',
    insights: {
      pain_emotion: apiTarget.insights?.pain_emotion || '',
      ideal_state: apiTarget.insights?.ideal_state || '',
      barriers: apiTarget.insights?.barriers || '',
      purchase_factor: apiTarget.insights?.purchase_factor || '',
    },
    benefits: {
      functional: apiTarget.benefits?.functional || '',
      emotional: apiTarget.benefits?.emotional || '',
      self_realization: apiTarget.benefits?.self_realization || '',
    },
    appeals: migrateAppeals(apiTarget.appeal || apiTarget.appeals),
    order: index,
  };
};

/** 旧データからセグメントへの変換 (新API形式対応) */
export const migrateToSegments = (
  target?: Target | any[],
  benefits?: Benefits,
  appeal?: Appeal
): Segment[] => {
  // 新API形式: target が配列の場合
  if (Array.isArray(target) && target.length > 0) {
    return target.map((t, index) => migrateTargetToSegment(t, index));
  }

  // 旧API形式: target が単一オブジェクトの場合
  if (!target && !benefits && !appeal) {
    return createDefaultSegments();
  }

  const segment: Segment = {
    id: nanoid(),
    name: (target as Target)?.segment || 'メインセグメント',
    funnel_stage: '',
    gender: '',
    age_range: '',
    region: '',
    occupation: '',
    purchasing_power: '',
    tech_literacy: '',
    life_stage: '',
    insights: {
      pain_emotion: (target as Target)?.insights?.pain_emotion || '',
      ideal_state: (target as Target)?.insights?.ideal_state || '',
      barriers: (target as Target)?.insights?.barriers || '',
      purchase_factor: (target as Target)?.insights?.purchase_factor || '',
    },
    benefits: {
      functional: benefits?.functional || '',
      emotional: benefits?.emotional || '',
      self_realization: benefits?.self_realization || '',
    },
    appeals: appeal ? [
      {
        id: nanoid(),
        name: '訴求1',
        type: 'functional',
        direction: appeal.direction || '',
        copy_example: appeal.winning_copy || '',
        order: 0,
      },
    ] : [createDefaultAppeal(0)],
    order: 0,
  };

  return [segment, createDefaultSegment(1), createDefaultSegment(2)];
};

// ========================================
// Extended Metadata (Updated)
// ========================================

export interface ProductExtendedMetadata {
  product?: ProductInfo;
  pricing_offer?: PricingOffer;
  segments?: Segment[];               // 新: 複数セグメント対応
  segment_master?: DcmSegmentMaster[];
  funnel_stage_master?: DcmFunnelStageMaster[];
  who?: DcmWho[];
  source?: DcmSource[];
  positioning?: Positioning;
  // 後方互換性のため残す（deprecated - segmentsに移行）
  target?: Target;
  benefits?: Benefits;
  appeal?: Appeal;
}

export interface Product {
  id: number;
  user_id: number;
  organization_id: number;
  name: string;
  url?: string;
  description?: string;
  category?: string;
  status: 'active' | 'inactive' | number;
  extended_metadata?: ProductExtendedMetadata;
  created_at: string;
  updated_at: string;
  competitors_count?: number;
  competitors?: Competitor[];
  product_images?: ProductImage[];
}

export interface Competitor {
  id: number;
  product_id: number;
  name: string;
  company_url?: string;
  description?: string;
  strengths?: string;
  weaknesses?: string;
  created_at: string;
  updated_at: string;
  monitored_urls_count?: number;
  monitored_urls?: MonitoredUrl[];
}

export interface MonitoredUrl {
  id: number;
  competitor_id: number;
  url: string;
  page_type: PageType;
  page_name: string;
  is_active: boolean;
  check_frequency_days: number;
  last_checked_at?: string;
  next_check_at?: string;
  status: MonitoringStatus;
  failure_count: number;
  created_at: string;
  updated_at: string;
  page_snapshots_count?: number;
  page_snapshots?: PageSnapshot[];
}

export type PageType =
  | 'corporate'
  | 'service'
  | 'lp'
  | 'pricing'
  | 'features'
  | 'about'
  | 'blog'
  | 'case_study'
  | 'contact'
  | 'recruit'
  | 'other';

export type MonitoringStatus =
  | 'pending'
  | 'checking'
  | 'success'
  | 'failed'
  | 'error';

export interface PageSnapshot {
  id: number;
  monitored_url_id: number;
  content_hash: string;
  html_content?: string;
  extracted_text?: string;
  metadata?: Record<string, any>;
  captured_at: string;
  created_at: string;
  updated_at: string;
}

export interface ChangeEvent {
  id: number;
  monitored_url_id: number;
  change_type: ChangeType;
  change_details?: Record<string, any>;
  severity: ChangeSeverity;
  before_snapshot_id?: number;
  after_snapshot_id?: number;
  detected_at: string;
  created_at: string;
  updated_at: string;
}

export type ChangeType =
  | 'content_added'
  | 'content_removed'
  | 'content_modified'
  | 'price_changed'
  | 'cta_changed'
  | 'page_structure'
  | 'metadata_changed'
  | 'other';

export type ChangeSeverity =
  | 'low'
  | 'medium'
  | 'high'
  | 'critical';

export interface KnowledgeNotification {
  id: number;
  user_id: number;
  change_event_id: number;
  type: NotificationType;
  title: string;
  message: string;
  is_read: boolean;
  sent_at?: string;
  read_at?: string;
  created_at: string;
  updated_at: string;
}

export type NotificationType =
  | 'email'
  | 'in_app'
  | 'slack'
  | 'webhook';

// API Request/Response types
export interface CreateProductRequest {
  name: string;
  description?: string;
  category?: string;
  status?: 'active' | 'inactive';
}

export interface UpdateProductRequest {
  name?: string;
  description?: string;
  category?: string;
  status?: 'active' | 'inactive';
}

export interface CreateCompetitorRequest {
  name: string;
  company_url?: string;
  description?: string;
  strengths?: string;
  weaknesses?: string;
}

export interface UpdateCompetitorRequest {
  name?: string;
  company_url?: string;
  description?: string;
  strengths?: string;
  weaknesses?: string;
}

export interface CreateMonitoredUrlRequest {
  url: string;
  page_type: PageType;
  page_name: string;
  is_active?: boolean;
  check_frequency_days?: number;
}

export interface UpdateMonitoredUrlRequest {
  url?: string;
  page_type?: PageType;
  page_name?: string;
  is_active?: boolean;
  check_frequency_days?: number;
}

// Filter types
export interface ProductFilters {
  status?: 'active' | 'inactive';
  category?: string;
  search?: string;
}

export interface MonitoredUrlFilters {
  active?: boolean;
  page_type?: PageType;
  status?: MonitoringStatus;
}

// Product Document
export interface ProductDocument {
  id: number;
  product_id: number;
  organization_id: number;
  file_name: string;
  file_type: string;
  file_size: number;
  s3_path: string;
  s3_bucket: string;
  mime_type: string;
  description?: string;
  source_type: string;
  google_drive_file_id?: string;
  uploaded_by: number;
  is_ai_context_enabled?: boolean;
  text_extracted_at?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  formatted_file_size: string;
  download_url?: string;
  uploader?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface CreateProductDocumentRequest {
  file: File;
  description?: string;
}

export interface UpdateProductDocumentRequest {
  description?: string;
}

export interface ProductDocumentFilters {
  file_type?: string;
  source_type?: string;
}

// Document Search
export interface DocumentSearchRequest {
  query: string;
  file_type?: 'pdf' | 'excel' | 'word';
  date_from?: string;
  date_to?: string;
}

export interface DocumentSearchCitation {
  chunk_name: string;
  content: string;
  relevance_score: number;
  document?: {
    id: number;
    file_name: string;
    file_type: string;
  };
}

export interface DocumentSearchResult {
  answer: string;
  citations: DocumentSearchCitation[];
  search_queries: string[];
}

// Dashboard statistics
export interface KnowledgeStats {
  total_products: number;
  total_competitors: number;
  total_monitored_urls: number;
  active_monitoring: number;
  recent_changes: number;
  failed_checks: number;
}
